/**
 * The CTA event vocabulary — one definition, read by both the components that
 * emit the events and the GA4 queries that report them.
 *
 * Why this file exists: the three conversion event names were hardcoded in
 * three separate components, and adding a reporting query would have made a
 * fourth copy. A renamed event would then silently vanish from the dashboard
 * while still firing, which is the exact drift the single-source rule exists
 * to prevent.
 *
 * Isomorphic on purpose — no imports, no browser or Node APIs — so the client
 * components and the server-side GA4 client can share it.
 */

/** Fired by every LINK CTA, via the delegated CtaTracker listener. */
export const CTA_CLICK_EVENT = 'cta_click'

/**
 * CTAs that convert by form submit rather than navigation.
 *
 * These deliberately do NOT fire `cta_click` — a submit is not a click, and
 * folding them into the click series would make click-through rate
 * meaningless. Each keeps the GA4 event name it has always had, because
 * renaming one would break its existing GA4 history.
 */
export const CTA_CONVERSIONS = [
  {
    event: 'strategy_call_request',
    ctaId: 'vector_pilot_form',
    label: 'Vector pilot request',
  },
  {
    event: 'hive_pilot_request',
    ctaId: 'hive_pilot_form',
    label: 'Hive pilot request',
  },
  {
    event: 'watch_domain_subscribed',
    ctaId: 'radar_watch_domain',
    label: 'Watch domain',
  },
] as const

export type CtaConversion = (typeof CTA_CONVERSIONS)[number]

// Named handles so each emitting component references its own entry by name
// rather than by array position.
export const VECTOR_PILOT = CTA_CONVERSIONS[0]
export const HIVE_PILOT = CTA_CONVERSIONS[1]
export const WATCH_DOMAIN = CTA_CONVERSIONS[2]

/** Event names only — the shape the GA4 `inListFilter` wants. */
export const CTA_CONVERSION_EVENTS: readonly string[] = CTA_CONVERSIONS.map(
  c => c.event
)

/** Human label for an event name, for dashboard rows. */
export function ctaConversionLabel(event: string): string {
  return CTA_CONVERSIONS.find(c => c.event === event)?.label ?? event
}
